import { useState, useEffect } from 'react';
import { useTelegram } from '../hooks/useTelegram';
import { 
  getAIAgents, createAIAgent, updateAIAgent, deleteAIAgent,
  getPlatformLimits, updatePlatformLimits 
} from '../utils/api';
import type { AIAgent, AIAgentTier, AIProviderType, PlatformLimits } from '@tg-miniapp/shared-types';

export function AdminAIPage(): React.JSX.Element {
  const { initData } = useTelegram();
  const [agents, setAgents] = useState<AIAgent[]>([]);
  const [limits, setLimits] = useState<PlatformLimits | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState<string | null>(null);

  const [formData, setFormData] = useState<Omit<AIAgent, 'id'>>({
    name: '',
    provider: 'openai-compatible',
    modelId: '',
    apiKey: '',
    baseUrl: '',
    isActive: true,
    tier: 'ALL'
  });

  useEffect(() => {
    loadData();
  }, [initData]);

  async function loadData() {
    if (!initData) return;
    try {
      const [agentsRes, limitsRes] = await Promise.all([
        getAIAgents(initData),
        getPlatformLimits(initData)
      ]);
      if (agentsRes.ok) setAgents(agentsRes.value.agents);
      if (limitsRes.ok) setLimits(limitsRes.value.limits);
    } catch (e) {
      setError('Ошибка загрузки данных');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleUpdateLimits() {
    if (!initData || !limits) return;
    setIsLoading(true);
    try {
      const res = await updatePlatformLimits(initData, limits);
      if (res.ok) alert('Лимиты обновлены!');
      else setError('Ошибка обновления лимитов');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!initData) return;
    setIsLoading(true);

    try {
      if (isEditing) {
        const res = await updateAIAgent(initData, isEditing, formData);
        if (res.ok) {
          setAgents(agents.map(a => a.id === isEditing ? res.value.agent : a));
          setIsEditing(null);
        }
      } else {
        const res = await createAIAgent(initData, formData);
        if (res.ok) setAgents([...agents, res.value.agent]);
      }
      setFormData({ name: '', provider: 'openai-compatible', modelId: '', apiKey: '', baseUrl: '', isActive: true, tier: 'ALL' });
    } finally {
      setIsLoading(false);
    }
  }

  async function handleDelete(id: string) {
    if (!initData || !confirm('Удалить агента?')) return;
    setIsLoading(true);
    try {
      const res = await deleteAIAgent(initData, id);
      if (res.ok) setAgents(agents.filter(a => a.id !== id));
    } finally {
      setIsLoading(false);
    }
  }

  function handleEdit(agent: AIAgent) {
    setIsEditing(agent.id);
    setFormData({
      name: agent.name,
      provider: agent.provider,
      modelId: agent.modelId,
      apiKey: agent.apiKey,
      baseUrl: agent.baseUrl || '',
      isActive: agent.isActive,
      tier: agent.tier
    });
  }

  if (isLoading && agents.length === 0) return <div className="p-4 text-center">Загрузка...</div>;

  return (
    <div className="flex flex-col gap-6 animate-in fade-in pb-20">
      <div>
        <h1 className="text-2xl font-bold bg-gradient-to-r from-tg-theme-text to-tg-theme-hint bg-clip-text text-transparent">
          🤖 Платформа: AI и Лимиты
        </h1>
        <p className="text-sm text-tg-theme-hint mt-1">
          Управление провайдерами и правилами тарифов.
        </p>
      </div>

      {error && (
        <div className="bg-red-500/10 text-red-500 p-3 rounded-xl text-sm border border-red-500/20">
          {error}
        </div>
      )}

      {/* Настройка лимитов тарифов */}
      {limits && (
        <div className="bg-tg-theme-bg p-5 rounded-2xl shadow-sm border border-black/5 dark:border-white/5">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            ⚙️ Глобальные лимиты тарифов
          </h2>
          <div className="flex flex-col gap-4">
            {(['FREE', 'START', 'PRO'] as const).map(plan => (
              <div key={plan} className="p-3 bg-black/5 dark:bg-white/5 rounded-xl">
                <div className="font-bold text-xs uppercase tracking-widest text-tg-theme-hint mb-2">{plan}</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-bold mb-1 block">AI Запросов/мес</label>
                    <input 
                      type="number"
                      className="w-full bg-tg-theme-bg rounded-lg px-3 py-2 text-sm border border-black/5 outline-none"
                      value={limits[plan].aiRequests}
                      onChange={e => setLimits({
                        ...limits,
                        [plan]: { ...limits[plan], aiRequests: parseInt(e.target.value) }
                      })}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold mb-1 block">Макс. клиентов</label>
                    <input 
                      type="number"
                      className="w-full bg-tg-theme-bg rounded-lg px-3 py-2 text-sm border border-black/5 outline-none"
                      value={limits[plan].maxClients}
                      onChange={e => setLimits({
                        ...limits,
                        [plan]: { ...limits[plan], maxClients: parseInt(e.target.value) }
                      })}
                    />
                  </div>
                </div>
              </div>
            ))}
            <button 
              onClick={handleUpdateLimits}
              className="mt-2 bg-blue-500 text-white font-bold py-3 rounded-xl text-sm transition-transform active:scale-95 shadow-lg shadow-blue-500/20"
            >
              Сохранить глобальные лимиты
            </button>
          </div>
        </div>
      )}

      {/* Форма создания/редактирования агентов */}
      <div className="bg-tg-theme-bg p-5 rounded-2xl shadow-sm border border-black/5 dark:border-white/5">
        <h2 className="text-lg font-bold mb-4">{isEditing ? '📝 Редактировать агента' : '➕ Добавить агента'}</h2>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="text-xs font-semibold text-tg-theme-hint uppercase tracking-wider mb-1 block">Название</label>
            <input
              required
              className="w-full bg-black/5 dark:bg-white/5 border border-transparent rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none transition-colors"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-tg-theme-hint uppercase tracking-wider mb-1 block">Провайдер</label>
              <select
                className="w-full bg-black/5 dark:bg-white/5 border border-transparent rounded-xl px-4 py-3 text-sm outline-none"
                value={formData.provider}
                onChange={(e) => setFormData({ ...formData, provider: e.target.value as AIProviderType })}
              >
                <option value="claude">Claude (Anthropic)</option>
                <option value="openrouter">OpenRouter</option>
                <option value="openai-compatible">OpenAI Compatible</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-tg-theme-hint uppercase tracking-wider mb-1 block">Тариф</label>
              <select
                className="w-full bg-black/5 dark:bg-white/5 border border-transparent rounded-xl px-4 py-3 text-sm outline-none"
                value={formData.tier}
                onChange={(e) => setFormData({ ...formData, tier: e.target.value as AIAgentTier })}
              >
                <option value="ALL">Всем</option>
                <option value="FREE">FREE</option>
                <option value="START">START</option>
                <option value="PRO">PRO</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-xs font-semibold text-tg-theme-hint uppercase tracking-wider mb-1 block">ID Модели</label>
              <input
                required
                className="w-full bg-black/5 dark:bg-white/5 border border-transparent rounded-xl px-4 py-3 text-sm outline-none"
                value={formData.modelId}
                onChange={(e) => setFormData({ ...formData, modelId: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-tg-theme-hint uppercase tracking-wider mb-1 block">API Ключ</label>
            <input
              required
              type="password"
              className="w-full bg-black/5 dark:bg-white/5 border border-transparent rounded-xl px-4 py-3 text-sm outline-none"
              value={formData.apiKey}
              onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
            />
          </div>

          {formData.provider === 'openai-compatible' && (
            <div>
              <label className="text-xs font-semibold text-tg-theme-hint uppercase tracking-wider mb-1 block">Base URL</label>
              <input
                required
                className="w-full bg-black/5 dark:bg-white/5 border border-transparent rounded-xl px-4 py-3 text-sm outline-none"
                value={formData.baseUrl}
                onChange={(e) => setFormData({ ...formData, baseUrl: e.target.value })}
              />
            </div>
          )}

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="isActive"
              className="w-5 h-5 rounded-lg accent-blue-500"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
            />
            <label htmlFor="isActive" className="text-sm font-medium">Активен</label>
          </div>

          <div className="flex gap-3 mt-2">
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-tg-theme-button text-tg-theme-button-text font-semibold py-3 rounded-xl disabled:opacity-50 transition-transform active:scale-95"
            >
              {isEditing ? 'Сохранить' : 'Добавить'}
            </button>
            {isEditing && (
              <button
                type="button"
                onClick={() => { setIsEditing(null); setFormData({ name: '', provider: 'openai-compatible', modelId: '', apiKey: '', baseUrl: '', isActive: true, tier: 'ALL' }); }}
                className="px-6 bg-black/5 dark:bg-white/5 font-semibold py-3 rounded-xl"
              >
                Отмена
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Список агентов */}
      <div className="flex flex-col gap-4">
        <h2 className="text-lg font-bold">Список агентов</h2>
        {agents.map((agent) => (
          <div key={agent.id} className="bg-tg-theme-bg p-4 rounded-2xl border border-black/5 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span>{agent.isActive ? '🟢' : '🔴'}</span>
                <span className="font-bold">{agent.name}</span>
              </div>
              <span className={`text-[10px] font-bold px-2 py-1 rounded-md uppercase ${
                agent.tier === 'PRO' ? 'bg-purple-500/10 text-purple-500' : 
                agent.tier === 'START' ? 'bg-orange-500/10 text-orange-500' :
                agent.tier === 'FREE' ? 'bg-green-500/10 text-green-500' : 'bg-blue-500/10 text-blue-500'
              }`}>
                {agent.tier}
              </span>
            </div>
            <div className="text-xs text-tg-theme-hint truncate">
              {agent.provider} | {agent.modelId}
            </div>
            <div className="flex gap-2">
              <button onClick={() => handleEdit(agent)} className="flex-1 bg-black/5 py-2 rounded-lg text-xs font-bold">Редактировать</button>
              <button onClick={() => handleDelete(agent.id)} className="flex-1 bg-red-500/5 text-red-500 py-2 rounded-lg text-xs font-bold">Удалить</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
